import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import styled from '@emotion/styled';
import { tokens, Button } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type {
  FilterKindEnum,
  FilterType,
  SingleFilterState,
  UpsellType,
} from '../../../domains/global-filters/model/FilterChain';
import { UpsellFooterEnum } from '../../../domains/global-filters/model/FilterChain';
import { hideOnEsc } from '../../../utils/hideOnEsc';
import { useTippy } from '../useTippy';
import { SingleFilterWrapper } from '../SingleFilterWrapper';
import { AddFilterDropdown, DROPDOWN_WIDTH, ONE_COLUMN_DROPDOWN_WIDTH } from './AddFilterDropdown';
import type { DateRange } from '../SingleFilter/DateRangeFilter/DateRangeFilterType';

export interface AddFilterTippyInstance {
  close: () => void;
  open: () => void;
}

const StyledTippy = styled(Tippy)`
  color: ${tokens.color.text_default};
  ${tokens.elevation(2)};
  background-color: ${tokens.color.ui_background};

  .tippy-content {
    padding: 0;
  }
`;

const LazyTippy = (props: TippyProps) => {
  const [mounted, setMounted] = useState(false);

  const computedProps = { ...props };
  const { content, render } = props;

  if (render) {
    computedProps.render = (...args) => (mounted ? render(...args) : '');
  } else {
    computedProps.content = mounted ? content : '';
  }

  return (
    <StyledTippy
      onMount={() => setMounted(true)}
      onHidden={() => setMounted(false)}
      {...computedProps}
    />
  );
};

interface AddFilterProps {
  filterStateByKind: Map<FilterKindEnum, SingleFilterState>;
  isMaxReached: boolean;
  onNewFilter: (newSingleFilter: FilterType) => void;
  showUpsellFooter?: UpsellFooterEnum;
  onUpsellClicked?: (upsellType: UpsellType) => void;
  onDropdownFilterMenuShown?: () => void;
  setAddFilterTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
  usePrimaryStyledAddFilters?: boolean;
  isFiltersDropdownVisibleByDefault?: boolean;
  autoFocusAddFilter?: boolean;
  dateRange?: DateRange;
  isScaleTrial?: boolean;
  isFunnelStep?: boolean;
}

const Z_INDEX = 13; // stay under .sidebar-feature and above .paging-row

export const AddFilter = ({
  filterStateByKind,
  isMaxReached,
  onNewFilter,
  showUpsellFooter,
  onUpsellClicked,
  onDropdownFilterMenuShown,
  setAddFilterTippyInstance,
  usePrimaryStyledAddFilters,
  isFiltersDropdownVisibleByDefault = false,
  autoFocusAddFilter = false,
  dateRange,
  isScaleTrial,
  isFunnelStep,
}: AddFilterProps): JSX.Element => {
  const { t } = useTranslation('global-filters');
  const tippy = useTippy();
  const [autoFocus, setAutoFocus] = useState(autoFocusAddFilter);
  const [dirtyValue, setDirtyValue] = useState<FilterType>();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' && isDropdownVisible === true) {
      const dropdownContent = tippy.get()?.popper;
      const firstFocusableElement = dropdownContent?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) as HTMLInputElement | null;
      firstFocusableElement?.focus();
    }
  };
  const hasOneColumn =
    isFunnelStep === true &&
    showUpsellFooter !== UpsellFooterEnum.UnlockBusinessFilter &&
    showUpsellFooter !== UpsellFooterEnum.UnlockPlusFilter &&
    showUpsellFooter !== UpsellFooterEnum.UnlockScaleFilter;

  if (dirtyValue) {
    return (
      <SingleFilterWrapper
        autoFocus
        currentValue={dirtyValue}
        onApply={(newValue) => {
          setDirtyValue(undefined);
          onNewFilter(newValue);
        }}
        onRemove={() => {
          setDirtyValue(undefined);
        }}
        onDropdownHidden={() => {
          setAutoFocus(true);
          if (dirtyValue?.value == null) {
            setDirtyValue(undefined);
          }
        }}
        showDropdownOnMount
        filterStateByKind={filterStateByKind}
        dateRange={dateRange}
        isScaleTrial={isScaleTrial}
        isFunnelStep={isFunnelStep}
      />
    );
  }

  const label = isFunnelStep === true ? t('addStep') : t('addFilter');
  const ariaLabel = isFunnelStep === true ? t('selectStep') : t('addFilter');

  return (
    <LazyTippy
      arrow={false}
      content={
        <AddFilterDropdown
          filterStateByKind={filterStateByKind}
          onNewFilter={setDirtyValue}
          isMaxReached={isMaxReached}
          showUpsellFooter={showUpsellFooter}
          onUpsellClicked={onUpsellClicked}
          isFunnelStep={isFunnelStep}
          hasOneColumn={hasOneColumn}
        />
      }
      offset={[0, 3]}
      interactive
      onCreate={(instance) => {
        tippy.set(instance);
        setAddFilterTippyInstance?.({
          open: instance.show,
          close: instance.hide,
        });
      }}
      onHide={() => setIsDropdownVisible(false)}
      onShow={() => setIsDropdownVisible(true)}
      onDestroy={() => setAddFilterTippyInstance?.(null)}
      showOnCreate={isFiltersDropdownVisibleByDefault}
      placement="bottom-start"
      theme="custom"
      trigger="click"
      zIndex={Z_INDEX}
      popperOptions={{
        modifiers: [{ name: 'flip', enabled: false }],
      }}
      appendTo={() => document.body} // To avoid overlapping issue (cf FN-1280)
      plugins={[hideOnEsc]}
      maxWidth={hasOneColumn ? ONE_COLUMN_DROPDOWN_WIDTH : DROPDOWN_WIDTH}
      onShown={onDropdownFilterMenuShown}
    >
      <Button
        autoFocus={autoFocus}
        kind={usePrimaryStyledAddFilters === true ? 'primary' : 'secondary'}
        icon="plus"
        aria-label={ariaLabel}
        size="s"
        onKeyUp={onKeyUp}
      >
        {label}
      </Button>
    </LazyTippy>
  );
};
